/**
 * The KnowIt adapter for share grants (W9 §A.3, §C.3): the authenticated management verbs
 * (`share-grants.list/.create/.update/.revoke`) and the three anonymous `*.viaGrant` reads that
 * power the public door at `/p/$urlId`.
 *
 * **`share-grants.resolve` is never called from here, on purpose (§E-8).** It answers a real
 * 401 for a password prompt and a real 410 for a dead link; this adapter's transport turns a
 * 401 into `onUnauthorized()` → a sign-in redirect, which would bounce an anonymous visitor off
 * a page that is supposed to need no session at all. The `*.viaGrant` family exists precisely
 * to answer a soft `200 {status}` envelope instead — `read()` returns a discriminated union,
 * never a throw, for exactly that reason.
 *
 * The anonymous calls ride the SAME transport as every other authenticated call: with no PKCE
 * session, `getAccessToken()` resolves `null` and no `Authorization` header is sent — there is
 * no separate "anonymous mode" to get wrong.
 */
import { ForbiddenError } from "~/api/errors";
import type {
  GrantComment,
  GrantRead,
  ShareGrant,
  ShareGrantDraft,
  ShareGrantPatch,
} from "~/api/types";
import { GrantCommentSchema, GrantReadSchema, ShareGrantSchema } from "~/api/types";

type Json = Record<string, unknown>;

function obj(value: unknown): Json {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Json) : {};
}
function arr(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

export interface GrantsTransport {
  post(path: string, body?: Record<string, unknown>): Promise<unknown>;
}

export function createKnowItGrants(transport: GrantsTransport) {
  return {
    list: async (documentId: string): Promise<ShareGrant[]> => {
      const raw = obj(await transport.post("share-grants.list", { documentId }));
      return arr(raw.data).map((entry) => ShareGrantSchema.parse(entry));
    },

    create: async (input: ShareGrantDraft): Promise<ShareGrant> => {
      const raw = obj(
        await transport.post("share-grants.create", {
          documentId: input.documentId,
          linkType: input.linkType ?? "view",
          ...(input.recipientContactId ? { recipientContactId: input.recipientContactId } : {}),
          ...(input.recipientEmail ? { recipientEmail: input.recipientEmail } : {}),
          ...(input.recipientName ? { recipientName: input.recipientName } : {}),
          ...(input.expiresAt ? { expiresAt: input.expiresAt } : {}),
          ...(input.password ? { password: input.password } : {}),
        }),
      );
      return ShareGrantSchema.parse(obj(raw.data));
    },

    update: async (input: ShareGrantPatch & { id: string }): Promise<ShareGrant> => {
      const raw = obj(
        await transport.post("share-grants.update", {
          id: input.id,
          ...(input.linkType ? { linkType: input.linkType } : {}),
          ...(input.expiresAt ? { expiresAt: input.expiresAt } : {}),
          ...(input.clearExpiry ? { clearExpiry: true } : {}),
          ...(input.password ? { password: input.password } : {}),
          ...(input.clearPassword ? { clearPassword: true } : {}),
        }),
      );
      return ShareGrantSchema.parse(obj(raw.data));
    },

    revoke: async (id: string): Promise<void> => {
      await transport.post("share-grants.revoke", { id });
    },

    /** Never throws for a dead or gated link — `status` carries that (§C.3 rule 1). */
    read: async (input: { urlId: string; password?: string }): Promise<GrantRead> => {
      const raw = obj(
        await transport.post("documents.read.viaGrant", {
          urlId: input.urlId,
          ...(input.password ? { password: input.password } : {}),
        }),
      );
      return GrantReadSchema.parse(obj(raw.data));
    },

    comments: async (input: { urlId: string; password?: string }): Promise<GrantComment[]> => {
      const raw = obj(
        await transport.post("documents.comments.viaGrant", {
          urlId: input.urlId,
          ...(input.password ? { password: input.password } : {}),
        }),
      );
      const data = obj(raw.data);
      if (data.status !== "ok") {
        return [];
      }
      return arr(data.comments).map((entry) => GrantCommentSchema.parse(entry));
    },

    comment: async (input: {
      urlId: string;
      password?: string;
      authorName?: string;
      text: string;
    }): Promise<GrantComment> => {
      const raw = obj(
        await transport.post("documents.comment.viaGrant", {
          urlId: input.urlId,
          ...(input.password ? { password: input.password } : {}),
          ...(input.authorName ? { authorName: input.authorName } : {}),
          text: input.text,
        }),
      );
      const data = obj(raw.data);
      if (data.status !== "ok") {
        // The server's own refusal (a View grant, e.g.) already threw before this point via the
        // transport's normal error mapping; a non-"ok" status reaching here is unexpected.
        throw new ForbiddenError("This link does not allow commenting.");
      }
      return GrantCommentSchema.parse(data.comment);
    },
  };
}
